import { ArrowUpRightIcon } from '@phosphor-icons/react';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { VOICE_ARCHETYPE_LIST } from '@/lib/ai-agent/prompts/voice-archetypes';
import type { VoiceMode } from '@/lib/ai-agent/prompts/voice-injection';
import { SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export const VOICE_BRAND_VALUE = 'brand';
export const VOICE_OFF_VALUE = 'off';
export const VOICE_ARCHETYPE_PREFIX = 'archetype:';
export const VOICE_BRAND_ID_PREFIX = 'brand:';

export function voiceModeToValue(mode: VoiceMode, brandVoiceId: string | null = null): string {
  if (mode.kind === 'brand') {
    return brandVoiceId ? `${VOICE_BRAND_ID_PREFIX}${brandVoiceId}` : VOICE_BRAND_VALUE;
  }
  if (mode.kind === 'off') return VOICE_OFF_VALUE;
  return `${VOICE_ARCHETYPE_PREFIX}${mode.id}`;
}

export interface ParsedVoiceValue {
  mode: VoiceMode;
  brandVoiceId: string | null;
}

export function valueToVoiceMode(value: string): ParsedVoiceValue | null {
  if (value === VOICE_BRAND_VALUE) return { mode: { kind: 'brand' }, brandVoiceId: null };
  if (value === VOICE_OFF_VALUE) return { mode: { kind: 'off' }, brandVoiceId: null };
  if (value.startsWith(VOICE_BRAND_ID_PREFIX)) {
    return {
      mode: { kind: 'brand' },
      brandVoiceId: value.slice(VOICE_BRAND_ID_PREFIX.length),
    };
  }
  if (value.startsWith(VOICE_ARCHETYPE_PREFIX)) {
    const id = value.slice(VOICE_ARCHETYPE_PREFIX.length);
    const archetype = VOICE_ARCHETYPE_LIST.find((a) => a.id === id);
    if (archetype) return { mode: { kind: 'archetype', id: archetype.id }, brandVoiceId: null };
  }
  return null;
}

export interface BrandVoicePickerVoice {
  id: string;
  profile_name: string;
  is_default: boolean;
  tone_descriptors: string[];
}

export interface BrandVoicePickerProps {
  value: VoiceMode;
  activeBrandVoiceId: string | null;
  onChange: (next: ParsedVoiceValue) => void;
  voices: BrandVoicePickerVoice[];
  archetypeAllowed?: boolean;
  offAllowed?: boolean;
  compact?: boolean;
  ariaLabel?: string;
  onEditClick?: () => void;
  onAddVoiceClick?: () => void;
  className?: string;
}

const COMPACT_TRIGGER_CLS =
  'h-8 w-full border-0 bg-transparent px-2 py-1.5 text-caption hover:bg-muted/40 focus:ring-0 focus:ring-offset-0 data-[state=open]:bg-muted/40 [&>svg]:opacity-60';

export function BrandVoicePicker({
  value,
  activeBrandVoiceId,
  onChange,
  voices,
  archetypeAllowed = true,
  offAllowed = true,
  compact = false,
  ariaLabel = 'Active brand voice',
  onEditClick,
  onAddVoiceClick,
  className,
}: BrandVoicePickerProps): JSX.Element {
  const hasVoices = voices.length > 0;
  const singleVoice = voices.length === 1 ? voices[0] : null;

  if (!hasVoices && onAddVoiceClick) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onAddVoiceClick();
        }}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-caption text-muted-foreground hover:bg-muted/40 hover:text-foreground',
          className,
        )}
        aria-label="Add brand voice"
      >
        <span className="text-caption font-normal text-foreground/85">Brand voice</span>
        <span className="flex items-center gap-1 text-detail text-muted-foreground/70">
          <span>Add voice</span>
          <ArrowUpRightIcon className="size-3" weight="regular" />
        </span>
      </button>
    );
  }

  const triggerLabel = (() => {
    if (value.kind === 'off') return 'Neutral';
    if (value.kind === 'archetype') {
      return VOICE_ARCHETYPE_LIST.find((a) => a.id === value.id)?.label ?? 'Voice';
    }
    if (activeBrandVoiceId) {
      return voices.find((v) => v.id === activeBrandVoiceId)?.profile_name ?? 'Brand voice';
    }
    if (singleVoice) return singleVoice.profile_name;
    const def = voices.find((v) => v.is_default);
    return def?.profile_name ?? 'Brand voice';
  })();

  return (
    <Select
      value={voiceModeToValue(value, activeBrandVoiceId)}
      onValueChange={(v) => {
        const parsed = valueToVoiceMode(v);
        if (parsed) onChange(parsed);
      }}
    >
      <SelectTrigger
        className={cn(compact && COMPACT_TRIGGER_CLS, className)}
        aria-label={ariaLabel}
        onClick={(e) => e.stopPropagation()}
      >
        <SelectValue placeholder="Select a voice">
          <span className="text-caption text-foreground truncate">{triggerLabel}</span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {hasVoices && (
          <SelectGroup>
            <SelectLabel className={cn('px-2 py-1 pl-2', SECTION_LABEL)}>Your voice</SelectLabel>
            {voices.length > 1 ? (
              <>
                <SelectItem value={VOICE_BRAND_VALUE}>
                  <span className="text-caption text-foreground">Use my default voice</span>
                </SelectItem>
                {voices.map((voice) => (
                  <SelectItem key={voice.id} value={`${VOICE_BRAND_ID_PREFIX}${voice.id}`}>
                    <span className="flex flex-col">
                      <span className="text-caption text-foreground">
                        {voice.profile_name}
                        {voice.is_default ? (
                          <span className="ml-1 text-detail text-muted-foreground/70">
                            · default
                          </span>
                        ) : null}
                      </span>
                      {voice.tone_descriptors.length > 0 && (
                        <span className="text-detail text-muted-foreground line-clamp-1">
                          {voice.tone_descriptors.slice(0, 3).join(' · ')}
                        </span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </>
            ) : (
              <SelectItem value={VOICE_BRAND_VALUE}>
                <span className="text-caption text-foreground">
                  {singleVoice?.profile_name ?? 'Brand voice'}
                </span>
              </SelectItem>
            )}
          </SelectGroup>
        )}
        {archetypeAllowed && (
          <SelectGroup>
            <SelectLabel className={cn('px-2 py-1 pl-2', SECTION_LABEL)}>
              Tone archetypes
            </SelectLabel>
            {VOICE_ARCHETYPE_LIST.map((a) => (
              <SelectItem key={a.id} value={`${VOICE_ARCHETYPE_PREFIX}${a.id}`}>
                <span className="flex flex-col">
                  <span className="text-caption text-foreground">{a.label}</span>
                  <span className="text-detail text-muted-foreground line-clamp-1">{a.blurb}</span>
                </span>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {offAllowed && (
          <SelectGroup>
            <SelectLabel className={cn('px-2 py-1 pl-2', SECTION_LABEL)}>Off</SelectLabel>
            <SelectItem value={VOICE_OFF_VALUE}>
              <span className="text-caption text-foreground">Neutral (off)</span>
            </SelectItem>
          </SelectGroup>
        )}
        {onEditClick && (
          <>
            <div className="my-1 h-px bg-border/40" />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEditClick();
              }}
              onPointerDown={(e) => e.stopPropagation()}
              className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-muted/40 transition-colors"
            >
              <span className="text-caption font-normal text-foreground/85">Edit brand voice</span>
              <ArrowUpRightIcon className="size-3.5 text-muted-foreground/70" weight="regular" />
            </button>
          </>
        )}
      </SelectContent>
    </Select>
  );
}
